import { Plus, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import TodoItemComponent from "./TodoItem";

interface TodoTag {
  id: string;
  name: string;
  color: string;
}

interface TodoItem {
  id: string;
  text: string;
  completed: boolean;
  cancelled: boolean;
  priority: "low" | "medium" | "high" | "critical";
  todoTags: TodoTag[];
  estimatedTimeValue?: number;
  estimatedTimeUnit?: "minutes" | "hours" | "days";
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  todoCategoryId: string;
  isTask?: boolean;
}

interface TodoCategory {
  id: string;
  name: string;
  color: string;
  isTask?: boolean;
}

interface TodoCategoryProps {
  category: TodoCategory;
  todoItems: TodoItem[];
  onToggleComplete: (categoryId: string, itemId: string) => void;
  onCancelItem: (categoryId: string, itemId: string) => void;
  onEditItem: (categoryId: string, itemId: string) => void;
  onDeleteItem: (categoryId: string, itemId: string) => void;
  onQuickAddItem: (categoryId: string) => void;
  canAddItems?: boolean;
  canUpdateItems?: boolean;
  canDeleteItems?: boolean;
  canToggleItems?: boolean;
}

export default function TodoCategoryComponent({
  category,
  todoItems,
  onToggleComplete,
  onCancelItem,
  onEditItem,
  onDeleteItem,
  onQuickAddItem,
  canAddItems = true,
  canUpdateItems = true,
  canDeleteItems = true,
  canToggleItems = true,
}: TodoCategoryProps) {
  const navigate = useNavigate();
  const categoryItems = todoItems.filter((item) => item.todoCategoryId === category.id);
  const completedCount = categoryItems.filter((item) => item.completed).length;
  const totalCount = categoryItems.length;

  return (
    <div className="bg-white border border-gray-200 rounded-lg mb-4 overflow-hidden">
      <div
        className="px-3 sm:px-4 py-3 flex items-center justify-between gap-2"
        style={{
          backgroundColor: `${category.color}15`,
          borderLeft: `4px solid ${category.color}`,
        }}>
        <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{category.name}</h3>
          <span className="text-xs sm:text-sm text-gray-600 flex-shrink-0">
            ({completedCount}/{totalCount})
          </span>
        </div>
        {category.isTask ? (
          <button
            onClick={() => navigate("/tasks?tab=assigned-tasks")}
            className="px-2 sm:px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded-md text-xs sm:text-sm font-medium inline-flex items-center gap-1 transition-colors flex-shrink-0"
            title="Görevlere Git">
            <ExternalLink className="h-4 w-4" />
          </button>
        ) : canAddItems ? (
          <button
            onClick={() => onQuickAddItem(category.id)}
            className="px-2 sm:px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md text-xs sm:text-sm font-medium inline-flex items-center gap-1 transition-colors flex-shrink-0">
            <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden xs:inline">Ekle</span>
          </button>
        ) : null}
      </div>

      <div className="divide-y divide-gray-100">
        {categoryItems.length === 0 ? (
          <div className="px-3 sm:px-4 py-6 text-center">
            <p className="text-gray-500 text-sm mb-3">
              {category.isTask ? "Bu kategorideki yapılacaklar Görevler modülü tarafından yönetilir." : "Bu kategoride henüz görev yok."}
            </p>
            {!category.isTask && canAddItems && (
              <button
                onClick={() => onQuickAddItem(category.id)}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium inline-flex items-center justify-center gap-2">
                <Plus className="h-4 w-4" />
                İlk Görevi Ekle
              </button>
            )}
          </div>
        ) : (
          categoryItems.map((item) => (
            <TodoItemComponent
              key={item.id}
              item={item}
              onToggleComplete={onToggleComplete}
              onCancelItem={onCancelItem}
              onEditItem={onEditItem}
              onDeleteItem={onDeleteItem}
              canUpdate={canUpdateItems}
              canDelete={canDeleteItems}
              canToggle={canToggleItems}
            />
          ))
        )}
      </div>
    </div>
  );
}
